import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatPermitHours } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parse } from "date-fns";
import { DownloadIcon, UploadIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { toast } from "sonner";
import {
  useGetPresence,
  useQueryPresencePKSKPrefix,
} from "../../hooks/data/use-presence-data";
import { useUserId } from "../../hooks/useUserId";
import {
  deleteObjectFromS3,
  generateExpenseReport,
  getPresignedUploadUrl,
  sendPresenceEmail,
  uploadFileToS3,
} from "../../services/api";
import { PresenceTableName, useDynamoMutateItem } from "../../services/dynamo";
import {
  DayStringFormat,
  type Expense,
  type Mileage,
  type Presence,
  type PresenceMonth,
  type PresenceRange,
} from "../../types/presence";
import Divider from "../Divider";
import Typography from "../Typography";
import AttachmentItem from "./AttachmentItem";

type EmailTemplate = {
  month: number;
  year: number;
  triggerAnnulla?: () => void;
  triggerInvia: () => void;
};

type UploadEntry = {
  id: string;
  name: string;
  file: File;
  key?: string;
  uploading: boolean;
  error: boolean;
};

const NiceDateFormat = "dd/MM/yyyy";
const ACCEPTED_GENERIC_ATTACHMENT_MIME =
  "image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ods";

function getMessageDataHash(data: Presence[] = []) {
  return data
    .map((item) =>
      JSON.stringify({
        sk: item.sk,
        reason: item.reason,
        date: "date" in item ? item.date : undefined,
        startDate: "startDate" in item ? item.startDate : undefined,
        endDate: "endDate" in item ? item.endDate : undefined,
        hours: "hours" in item ? item.hours : undefined,
      }),
    )
    .sort()
    .join("|");
}

export default function EmailTemplateForm({
  triggerInvia,
  year,
  month,
}: EmailTemplate) {
  const formatRangeAbsenceMessage = useCallback(
    (label: string, startDay: string, endDay: string, variant?: string) => {
      const startDate = parse(startDay, DayStringFormat, new Date());
      const endDate = parse(endDay, DayStringFormat, new Date());
      const formattedStartDate = format(startDate, NiceDateFormat);
      const formattedEndDate = format(endDate, NiceDateFormat);

      if (formattedStartDate === formattedEndDate) {
        return variant === "assente"
          ? `Assente per ${label} il giorno ${formattedStartDate}`
          : `${label} il giorno ${formattedStartDate}`;
      }

      return variant === "assente"
        ? `Assente dal ${formattedStartDate} al ${formattedEndDate} per ${label}`
        : `${label} dal giorno ${formattedStartDate} al giorno ${formattedEndDate}`;
    },
    [],
  );

  const userId = useUserId();
  const { user } = useAuth();
  const strMonth = month.toString().padStart(2, "0");
  const [text, setText] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadEntries, setUploadEntries] = useState<UploadEntry[]>([]);
  const [removedSavedKeys, setRemovedSavedKeys] = useState<Set<string>>(
    new Set(),
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pk = `${userId}` as const;
  const sk =
    `presenceMonth#${year}-${strMonth}` as `presenceMonth#${number}-${number}`;
  const { data: monthRecord, isLoading: isLoadingMonth } =
    useGetPresence<PresenceMonth>(pk, sk);
  if (!isLoadingMonth && monthRecord?.sendDate) {
    throw new Error("Il mese è già stato inviato");
  }

  const { data, isLoading } = useQueryPresencePKSKPrefix<Presence>(
    pk,
    `presenceDay#${year}-${strMonth}`,
  );

  const { data: expenses = [], isLoading: isLoadingExpenses } =
    useQueryPresencePKSKPrefix<Expense>(pk, `expense#${year}-${strMonth}`);

  const { data: mileages = [], isLoading: isLoadingMileages } =
    useQueryPresencePKSKPrefix<Mileage>(pk, `mileage#${year}-${strMonth}`);

  const expenseAttachments = expenses.flatMap((e) => e.attachments ?? []);
  const totalCost = expenses.reduce((sum, e) => sum + (e.cost ?? 0), 0);
  const totalRefound = mileages.reduce((sum, m) => sum + (m.refound ?? 0), 0);
  const isUploading = uploadEntries.some((entry) => entry.uploading);
  const savedAttachments = (monthRecord?.attachments ?? []).filter(
    (key) => !removedSavedKeys.has(key),
  );
  const uploadedKeys = uploadEntries
    .filter((entry) => entry.key)
    .map((entry) => entry.key!);

  // Deduplicate medical certificate keys across repeated malattia day records
  const seenCertKeys = new Set<string>();
  const certItems = (data ?? [])
    .filter(
      (d): d is PresenceRange =>
        d.reason === "malattia" &&
        ((d as PresenceRange).attachments?.length ?? 0) > 0,
    )
    .flatMap((d) =>
      (d.attachments ?? []).map((key) => ({
        key,
        date: (d.date ?? d.startDate) as string,
      })),
    )
    .filter(({ key }) => {
      if (seenCertKeys.has(key)) return false;
      seenCertKeys.add(key);
      return true;
    });
  const setCalcMessage = useCallback(() => {
    let message = data!
      .reduce((acc, item) => {
        switch (item.reason) {
          case "malattia":
          case "ferie":
          case "festività":
          case "chiusura ufficio":
          case "maternità":
            if (
              !acc.some(
                (el) =>
                  el.reason === item.reason &&
                  el.startDate === item.startDate &&
                  el.endDate === item.endDate,
              )
            ) {
              acc.push(item);
            }
            break;
          default:
            acc.push(item);
        }
        return acc;
      }, [] as Presence[])
      .map((item) => {
        switch (item.reason) {
          case "ferie": {
            return formatRangeAbsenceMessage(
              "ferie",
              item.startDate,
              item.endDate,
              "assente",
            );
          }
          case "festività": {
            return formatRangeAbsenceMessage(
              "festività",
              item.startDate,
              item.endDate,
              "assente",
            );
          }
          case "chiusura ufficio": {
            return formatRangeAbsenceMessage(
              "chiusura ufficio",
              item.startDate,
              item.endDate,
              "assente",
            );
          }
          case "maternità": {
            return formatRangeAbsenceMessage(
              "Maternità",
              item.startDate,
              item.endDate,
            );
          }
          case "permesso": {
            const date = parse(item.date, DayStringFormat, new Date());
            return `Permesso il giorno ${format(date, NiceDateFormat)} per ${formatPermitHours(
              item.hours ?? 0,
            )}`;
          }
          case "104": {
            const date = parse(item.date, DayStringFormat, new Date());
            return `Permesso 104 il giorno ${format(date, NiceDateFormat)} per ${formatPermitHours(
              item.hours ?? 0,
            )}`;
          }
          case "malattia": {
            return formatRangeAbsenceMessage(
              "Malattia",
              item.startDate,
              item.endDate,
            );
          }
          default:
            return "";
        }
      })
      .join("\n");
    message += "\n\nPresente Altrimenti.";
    setText(message);
  }, [data, formatRangeAbsenceMessage]);
  useEffect(() => {
    if (isLoadingMonth || isLoading) return;
    const currentDataHash = getMessageDataHash(data ?? []);
    const savedDataHash = monthRecord?.messageDataHash as string | undefined;
    if (monthRecord?.message && savedDataHash === currentDataHash) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setText(monthRecord.message);
    } else if (data!.length > 0) {
      setCalcMessage();
    } else {
      setText("Sempre presente");
    }
  }, [data, isLoading, isLoadingMonth, monthRecord, setCalcMessage]);

  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useDynamoMutateItem<PresenceMonth>(
    PresenceTableName,
    {
      mutationKey: ["dynamo", PresenceTableName, pk, sk],
    },
  );

  const { mutateAsync: sendEmailMutate, isPending: isSendPending } = useMutation({
    mutationFn: async (test: boolean) => {
      const name =
        user?.profile?.name ||
        `${user?.profile?.given_name} ${user?.profile?.family_name}`;
      await sendPresenceEmail({
        monthSk: sk,
        test,
        userEmail: user?.profile?.email!,
        userName: name as string,
        idToken: user?.id_token!,
      });
      return test;
    },
    onSuccess: (isTest) => {
      if (isTest) {
        toast.success("Email di test inviata alla tua casella. Controlla la posta.");
      } else {
        queryClient.setQueriesData<PresenceMonth | null>(
          { queryKey: ["dynamo", PresenceTableName, "item", `${userId}`, sk] },
          (current) => (current ? { ...current, sendDate: Date.now() } : current),
        );
        queryClient.invalidateQueries({
          queryKey: ["dynamo", PresenceTableName, "item", `${userId}`, sk],
        });
        toast.success("Report inviato con successo!");
        triggerInvia();
      }
    },
  });

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";

    for (const file of files) {
      const id = crypto.randomUUID();
      setUploadEntries((prev) => [
        ...prev,
        { id, name: file.name, file, uploading: true, error: false },
      ]);
      try {
        const { url, key } = await getPresignedUploadUrl({
          year,
          month,
          filename: file.name,
          contentType: file.type || "application/octet-stream",
        });
        await uploadFileToS3(url, file);
        setUploadEntries((prev) =>
          prev.map((entry) =>
            entry.id === id ? { ...entry, key, uploading: false } : entry,
          ),
        );
        toast.success(`${file.name} caricato con successo`);
      } catch {
        setUploadEntries((prev) =>
          prev.map((entry) =>
            entry.id === id
              ? { ...entry, uploading: false, error: true }
              : entry,
          ),
        );
        toast.error(`Errore nel caricamento di ${file.name}`);
      }
    }
  };

  const handleRemoveUploadEntry = async (entry: UploadEntry) => {
    setUploadEntries((prev) => prev.filter((candidate) => candidate.id !== entry.id));
    if (entry.key) {
      try {
        await deleteObjectFromS3(entry.key);
      } catch {
        // silently ignore
      }
    }
  };

  const handleRemoveSavedAttachment = async (key: string) => {
    setRemovedSavedKeys((prev) => new Set([...prev, key]));
    toast.info(
      'Allegato rimosso. Clicca "Invia" per rendere la modifica definitiva.',
    );
    try {
      await deleteObjectFromS3(key);
    } catch {
      // silently ignore
    }
  };

  const handleDownloadReport = async () => {
    const idToken = user?.id_token;
    if (!idToken) return;
    const firstName = user?.profile?.given_name as string | undefined;
    const lastName = user?.profile?.family_name as string | undefined;
    const userName =
      lastName && firstName
        ? `${lastName} ${firstName}`
        : (lastName ??
          firstName ??
          (user?.profile?.name as string | undefined) ??
          "Dipendente");
    setIsDownloading(true);
    try {
      await generateExpenseReport({
        monthSk: sk,
        userName,
        idToken,
        expenses,
        mileages,
      });
      toast.success("Report scaricato!");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Errore durante il download",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading || isLoadingMonth || isLoadingExpenses || isLoadingMileages)
    return (
      <div className="SendModal__loading">
        <Typography variant="body1">Caricamento...</Typography>
      </div>
    );

  const handleSend = async (test: boolean) => {
    const messageDataHash = getMessageDataHash(data ?? []);
    try {
      await mutateAsync({
        pk,
        sk,
        year: monthRecord?.year ?? year,
        month: monthRecord?.month ?? month,
        days: monthRecord?.days ?? [],
        ...(monthRecord ?? {}),
        message: text,
        messageDataHash,
        attachments: [...savedAttachments, ...uploadedKeys],
        withNotaSpese: expenses.length > 0 || mileages.length > 0,
      });
      await sendEmailMutate(test);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Errore durante il salvataggio. Riprova.",
      );
    }
  };
  return (
    <div className="SendModal">
      <div className="SendModal__header">
        <Typography variant="h5" className="bold">
          Anteprima email — {String(month).padStart(2, "0")}/{year}
        </Typography>
        <Typography variant="body2">
          Modifica il testo se necessario, poi premi Invia.
        </Typography>
        <Divider />
      </div>

      <form
        id="email-template-form"
        className="SendModal__body"
      >
        <Textarea
          id="standard-multiline-static"
          placeholder="Testo della mail"
          rows={10}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={isPending || isSendPending}
        />
        <div className="SendModal__attachments">
          <Typography variant="h6" className="bold">
            Allegato generico
          </Typography>
          <Typography variant="body2">
            Puoi allegare documenti, fogli Excel o immagini da includere nella mail.
          </Typography>
          {(savedAttachments.length > 0 || uploadEntries.length > 0) && (
            <ul className="SendModal__attachments-list">
              {savedAttachments.map((key) => (
                <AttachmentItem
                  key={key}
                  name={key.split("/").pop() ?? key}
                  s3Key={key}
                  onRemove={
                    isPending || isUploading
                      ? undefined
                      : () => handleRemoveSavedAttachment(key)
                  }
                />
              ))}
              {uploadEntries.map((entry) => (
                <AttachmentItem
                  key={entry.id}
                  name={entry.name}
                  file={entry.file}
                  uploading={entry.uploading}
                  error={entry.error}
                  onRemove={
                    !entry.uploading && !isPending
                      ? () => handleRemoveUploadEntry(entry)
                      : undefined
                  }
                />
              ))}
            </ul>
          )}
          {savedAttachments.length === 0 && uploadEntries.length === 0 && !isUploading && (
            <Typography variant="body2">Nessun allegato generico</Typography>
          )}
          <div className="SendModal__upload">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_GENERIC_ATTACHMENT_MIME}
              className="ExpenseForm__file-input"
              onChange={handleFileChange}
              disabled={isPending || isSendPending || isUploading}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isPending || isSendPending || isUploading}
            >
              <UploadIcon className="h-4 w-4 mr-1" />
              {isUploading ? "Caricamento..." : "Scegli file"}
            </Button>
          </div>
        </div>
        {certItems.length > 0 && (
          <div className="SendModal__attachments">
            <Typography variant="h6" className="bold">
              Dettaglio certificati medici
            </Typography>
            <Typography variant="body2">
              Verranno allegati automaticamente {certItems.length} certificat
              {certItems.length === 1 ? "o" : "i"} medic
              {certItems.length === 1 ? "o" : "i"}.
            </Typography>
            <ul className="SendModal__attachments-list">
              {certItems.map(({ key, date }) => (
                <AttachmentItem
                  key={key}
                  name={key.split("/").pop() ?? key}
                  s3Key={key}
                  label={format(
                    parse(date, DayStringFormat, new Date()),
                    NiceDateFormat,
                  )}
                />
              ))}
            </ul>
          </div>
        )}
      </form>

      <div className="SendModal__expenses">
        <Typography variant="h6" className="bold">
          Dettaglio note spese
        </Typography>

        <Typography variant="body2">
          Qui puoi vedere il dettaglio delle note spese che verranno allegate
          alla mail.
        </Typography>
        <div className="SendModal__expenses-list">
          {expenses
            .slice()
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((e) => (
              <div key={e.sk} className="SendModal__expenses-row">
                <Typography variant="body2">
                  {format(
                    parse(e.date, DayStringFormat, new Date()),
                    NiceDateFormat,
                  )}
                </Typography>
                <Typography variant="body2">{e.description ?? "—"}</Typography>
                <Typography variant="body2">€{e.cost.toFixed(2)}</Typography>
              </div>
            ))}
          <div className="SendModal__expenses-total">
            <Typography variant="body2" className="bold">
              Totale:
            </Typography>
            <Typography variant="body2" className="bold">
              €{totalCost.toFixed(2)}
            </Typography>
          </div>
        </div>
        {expenseAttachments.length > 0 ? (
          <div className="SendModal__attachments">
            <Typography variant="body2">
              Verranno allegati automaticamente {expenseAttachments.length}{" "}
              allegat{expenseAttachments.length === 1 ? "o" : "i"} dalla nota
              spese del mese.
            </Typography>
            <ul className="SendModal__attachments-list">
              {expenses
                .filter((e) => (e.attachments?.length ?? 0) > 0)
                .slice()
                .sort((a, b) => a.date.localeCompare(b.date))
                .flatMap((expense) =>
                  (expense.attachments ?? []).map((key) => (
                    <AttachmentItem
                      key={key}
                      name={key.split("/").pop() ?? key}
                      s3Key={key}
                      label={format(
                        parse(expense.date, DayStringFormat, new Date()),
                        NiceDateFormat,
                      )}
                    />
                  )),
                )}
            </ul>
          </div>
        ) : (
          <Typography variant="body2">
            Nessuna nota spese caricata per questo mese.
          </Typography>
        )}
      </div>

      <div className="SendModal__expenses">
        <Typography variant="h6" className="bold">
          Dettaglio rimborsi kilometrici
        </Typography>

        <Typography variant="body2">
          Qui puoi vedere il dettaglio dei rimborsi kilometrici del mese.
        </Typography>
        <div className="SendModal__expenses-list">
          {mileages.length === 0 ? (
            <Typography variant="body2">
              Nessun rimborso kilometrico registrato per questo mese.
            </Typography>
          ) : (
            <>
              {mileages
                .slice()
                .sort((a, b) => a.date.localeCompare(b.date))
                .map((m) => (
                  <div key={m.sk} className="SendModal__expenses-row">
                    <Typography variant="body2">
                      {format(
                        parse(m.date, DayStringFormat, new Date()),
                        NiceDateFormat,
                      )}
                    </Typography>
                    <Typography variant="body2">
                      {m.destination}
                      {m.client ? ` — ${m.client}` : ""}
                    </Typography>
                    <Typography variant="body2">{m.distance} km</Typography>
                    <Typography variant="body2">
                      €{m.refound.toFixed(2)}
                    </Typography>
                  </div>
                ))}
              <div className="SendModal__expenses-total">
                <Typography variant="body2" className="bold">
                  Totale:
                </Typography>
                <Typography variant="body2" className="bold">
                  €{totalRefound.toFixed(2)}
                </Typography>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="SendModal__actions">
        <Button
          type="button"
          variant="outline"
          onClick={handleDownloadReport}
          disabled={isDownloading || isPending || isSendPending || isUploading}
        >
          <DownloadIcon className="h-4 w-4 mr-1" />
          {isDownloading ? "Generazione..." : "Scarica Report"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={isPending || isSendPending || isUploading}
          onClick={() => handleSend(true)}
        >
          Invia test (a te)
        </Button>
        <Button
          type="button"
          disabled={isPending || isSendPending || isUploading}
          onClick={() => handleSend(false)}
        >
          Conferma e invia
        </Button>
      </div>
    </div>
  );
}
